import { useState } from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { ChevronDown, Phone, Mail } from "lucide-react";
import { Link } from "react-router-dom";

const faqs = [
  {
    category: "Tax Preparation",
    questions: [
      {
        q: "What documents do I need to file my taxes?",
        a: "You'll need your W-2s from employers, 1099 forms for freelance/contract income, Social Security numbers for yourself and dependents, last year's tax return, and any receipts for deductible expenses. Our intake form will guide you through exactly what's needed."
      },
      {
        q: "How long does it take to prepare my tax return?",
        a: "Most returns are completed within 3-5 business days after we receive all your documents. Complex returns (business owners, multiple income sources) may take 5-7 business days."
      },
      {
        q: "Do you offer in-person appointments?",
        a: "We primarily offer virtual services nationwide. However, you can schedule a phone or video consultation to discuss your tax situation in detail."
      },
      {
        q: "What's your fee structure?",
        a: "Our fees vary based on the complexity of your return. Simple individual returns start at competitive rates, while business and complex returns are quoted after reviewing your situation. We'll always provide a clear quote before beginning work."
      },
      {
        q: "Can you help if I haven't filed taxes in several years?",
        a: "Yes! We specialize in helping clients catch up on unfiled returns. We'll work with you to gather the necessary documents and file all outstanding returns."
      },
    ]
  },
  {
    category: "Credit Education",
    questions: [
      {
        q: "What is credit education coaching?",
        a: "Our credit education program teaches you how credit works, factors that affect your score, and strategies to build or improve your credit over time. This is educational coaching, not credit repair services."
      },
      {
        q: "How long does it take to improve my credit score?",
        a: "Credit improvement is a gradual process. With consistent positive habits, you may see improvements in 3-6 months. Significant changes typically take 6-12 months or longer depending on your starting point."
      },
      {
        q: "Do you dispute items on my credit report?",
        a: "Our service focuses on education and coaching. We teach you how to review your credit reports, understand what's affecting your score, and the proper steps to address inaccuracies yourself."
      },
    ]
  },
  {
    category: "Payments & Refunds",
    questions: [
      {
        q: "When will I receive my tax refund?",
        a: "The IRS typically issues refunds within 21 days of accepting an e-filed return. Direct deposit is the fastest method. Paper checks take longer. We'll provide you with tracking information once your return is filed."
      },
      {
        q: "What payment methods do you accept?",
        a: "We accept credit/debit cards, bank transfers, and payment from your refund (if applicable). Payment plans may be available for larger balances."
      },
      {
        q: "Can I pay your fee from my refund?",
        a: "Yes, in many cases we can deduct our fee directly from your federal refund. This option will be discussed during the filing process."
      },
    ]
  },
  {
    category: "Referral Program",
    questions: [
      {
        q: "How does the referral program work?",
        a: "Refer a friend or family member to WMJ Enterprise Services. When they complete their tax filing with us and receive their refund, you'll receive $50 as a thank you for the referral."
      },
      {
        q: "Is there a limit to how many people I can refer?",
        a: "No limit! You can refer as many people as you'd like. Each successful referral earns you $50."
      },
      {
        q: "How do I make a referral?",
        a: "Simply have your referral mention your name when they complete their intake form. Make sure they include your name in the 'Referred By' field."
      },
    ]
  },
];

const FAQ = () => {
  const [openItems, setOpenItems] = useState({});

  const toggleItem = (categoryIndex, questionIndex) => {
    const key = `${categoryIndex}-${questionIndex}`;
    setOpenItems((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="faq-page">
      <Header />
      
      <main className="pt-[75px] md:pt-[90px] pb-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="font-heading font-bold text-4xl md:text-5xl text-navy mb-4">
              Frequently Asked Questions
            </h1>
            <p className="text-slate-600 text-lg">
              Find answers to common questions about our services.
            </p>
          </div>

          {/* FAQ Sections */}
          <div className="space-y-8">
            {faqs.map((section, categoryIndex) => (
              <div key={section.category} className="bg-white rounded-xl shadow-card overflow-hidden">
                <div className="bg-navy px-6 py-4">
                  <h2 className="font-heading font-bold text-xl text-white">
                    {section.category}
                  </h2>
                </div>
                <div className="divide-y divide-slate-100">
                  {section.questions.map((item, questionIndex) => {
                    const key = `${categoryIndex}-${questionIndex}`;
                    const isOpen = openItems[key];
                    return (
                      <div key={questionIndex}>
                        <button
                          onClick={() => toggleItem(categoryIndex, questionIndex)}
                          className="w-full flex items-center justify-between px-6 py-4 text-left hover:bg-slate-50 transition-colors"
                          data-testid={`faq-${categoryIndex}-${questionIndex}`}
                        >
                          <span className="font-medium text-navy pr-4">{item.q}</span>
                          <ChevronDown
                            className={`w-5 h-5 text-slate-400 flex-shrink-0 transition-transform ${
                              isOpen ? "rotate-180" : ""
                            }`}
                          />
                        </button>
                        {isOpen && (
                          <div className="px-6 pb-4">
                            <p className="text-slate-600 leading-relaxed">{item.a}</p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          {/* Contact Section */}
          <div className="mt-12 bg-gold/10 rounded-xl p-8 text-center">
            <h3 className="font-heading font-bold text-xl text-navy mb-2">
              Still have questions?
            </h3>
            <p className="text-slate-600 mb-6">
              Our team is here to help. Reach out to us directly.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href="tel:+19014013054"
                className="flex items-center gap-2 bg-navy text-white px-6 py-3 rounded-lg hover:bg-navy-light transition-colors"
              >
                <Phone className="w-4 h-4" />
                (901) 401-3054
              </a>
              <a
                href="mailto:info@wmjenterpriseservices.com"
                className="flex items-center gap-2 bg-white text-navy border border-navy px-6 py-3 rounded-lg hover:bg-navy hover:text-white transition-colors"
              >
                <Mail className="w-4 h-4" />
                Email Us
              </a>
            </div>
          </div>

          {/* CTA */}
          <div className="mt-12 text-center">
            <Link
              to="/intake"
              className="inline-flex items-center gap-2 bg-gold hover:bg-gold-dark text-navy font-semibold px-8 py-3 rounded-lg transition-colors"
              data-testid="faq-cta"
            >
              Start My Tax Intake
            </Link>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default FAQ;
